import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

import { getCandidateVideoInterviewUrl } from '@constants';

const CACHE_TTL_MS = 5 * 60 * 1000;
const cache = new Map<string, { url: Promise<string | null>; exp: number }>();

const MAX_CONCURRENT = 4;
let inFlight = 0;
const waiting: (() => void)[] = [];

const acquire = () =>
  new Promise<void>((resolve) => {
    if (inFlight < MAX_CONCURRENT) {
      inFlight += 1;
      resolve();
      return;
    }
    waiting.push(() => {
      inFlight += 1;
      resolve();
    });
  });

const release = () => {
  inFlight -= 1;
  waiting.shift()?.();
};

const requestUrl = (fileName: string): Promise<string | null> => {
  const cached = cache.get(fileName);
  if (cached && Date.now() < cached.exp) return cached.url;

  const pending = acquire()
    .then(() =>
      axios.get<string>(getCandidateVideoInterviewUrl(), {
        params: { fileName },
        withCredentials: true,
      }),
    )
    .then((response) => response.data || null)
    .catch(() => {
      cache.delete(fileName);
      return null;
    })
    .finally(release);

  cache.set(fileName, { url: pending, exp: Date.now() + CACHE_TTL_MS });
  return pending;
};

export const useVideoThumbnail = (fileName?: string | null) => {
  const ref = useRef<HTMLElement | null>(null);
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!fileName || !node) return undefined;

    let cancelled = false;
    const load = () => {
      requestUrl(fileName).then((value) => {
        if (!cancelled) setUrl(value);
      });
    };

    if (typeof IntersectionObserver === 'undefined') {
      load();
      return () => {
        cancelled = true;
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          load();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [fileName]);

  return { ref, url };
};

export const formatVideoDuration = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${String(secs).padStart(2, '0')}`;
};
